/// Provisioning + crew-brief content. Both are part of the
/// pre-departure phase so they live alongside the predeparture
/// checklist.
///
/// Two domains:
///
///   1. Provisioning rates — water / fuel / food / gas / battery
///      per person per day, with calculator helpers.
///   2. Crew safety brief — sectioned template covering MOB,
///      fire, abandon ship, jackstays + harness, watch system.

// ───────────────────────────────────────────────────────────────────
// 1. Provisioning rates + calculator
// ───────────────────────────────────────────────────────────────────

export type ConsumableId =
  | "water-drink"
  | "water-wash"
  | "food"
  | "fuel-cruise"
  | "fuel-charge"
  | "gas-cooking";

export interface ConsumableRate {
  id: ConsumableId;
  name: string;
  /// Unit the rate is expressed in.
  unit: string;
  /// Litres / kg / hours per person per day at the rated level.
  ratedPerPersonPerDay: number;
  /// Lower bound (minimal / emergency).
  minimumPerPersonPerDay: number;
  /// Generous comfortable rate.
  generousPerPersonPerDay: number;
  /// Operational notes.
  notes: string;
}

export const CONSUMABLES: readonly ConsumableRate[] = Object.freeze([
  {
    id: "water-drink",
    name: "Drinking water",
    unit: "litres / person / day",
    ratedPerPersonPerDay: 2.5,
    minimumPerPersonPerDay: 1.5,
    generousPerPersonPerDay: 4.0,
    notes:
      "Hot climates + active sailing raise the rate to 4–5 L. RYA recommendation is to plan for the typical day + carry 50% extra as emergency reserve. Avoid tap water in unfamiliar countries — use a filter or bottled water.",
  },
  {
    id: "water-wash",
    name: "Washing water (cooking + light wash)",
    unit: "litres / person / day",
    ratedPerPersonPerDay: 5.0,
    minimumPerPersonPerDay: 2.0,
    generousPerPersonPerDay: 10.0,
    notes:
      "Cooking, hand-washing, light personal hygiene. Showers consume MUCH more if not rationed. Ocean voyages typically ration washing water to ~2 L per person per day; coastal cruising 5–10 L.",
  },
  {
    id: "food",
    name: "Food",
    unit: "kg / person / day",
    ratedPerPersonPerDay: 1.0,
    minimumPerPersonPerDay: 0.5,
    generousPerPersonPerDay: 1.5,
    notes:
      "Roughly 1 kg per person per day for varied cooked food. Pack 30% extra for delays + weather. Calorie target 2500–3500 kcal/day for offshore crew; more in cold weather.",
  },
  {
    id: "fuel-cruise",
    name: "Engine fuel (cruise)",
    unit: "litres / engine-hour",
    ratedPerPersonPerDay: 2.5,
    minimumPerPersonPerDay: 1.5,
    generousPerPersonPerDay: 4.0,
    notes:
      "Typical 35–40 ft yacht with a 30-50 hp marine diesel — 2.5 L/h at cruising rpm. Modern lower-rpm engines closer to 1.5 L/h; older / harder-working installations 3+ L/h. ALWAYS plan with 30% reserve.",
  },
  {
    id: "fuel-charge",
    name: "Engine fuel (charging only)",
    unit: "litres / charging-hour",
    ratedPerPersonPerDay: 0.7,
    minimumPerPersonPerDay: 0.4,
    generousPerPersonPerDay: 1.0,
    notes:
      "Running the engine at idle / 1200 rpm for battery charging only — much less than cruise burn. 1 hour charging morning + evening on a coastal cruise = ~1.5 L/day.",
  },
  {
    id: "gas-cooking",
    name: "Gas (cooking)",
    unit: "grams / person / day",
    ratedPerPersonPerDay: 80,
    minimumPerPersonPerDay: 40,
    generousPerPersonPerDay: 150,
    notes:
      "Typical 4.5 kg propane bottle = ~56 person-days at the rated 80 g/person/day (so 4 crew × 14 days = 56 person-days = one bottle). Hot drinks + freshly cooked meals raise the rate; cold tinned food drops it.",
  },
]);

export interface ProvisioningCalc {
  consumable: ConsumableId;
  /// Total quantity for the planned passage in the consumable's
  /// natural unit (litres / kg / grams).
  totalQuantity: number;
  /// Per-day quantity at the rated level.
  perDay: number;
  /// Suggested safety reserve to add on top (30% by default).
  reserveQuantity: number;
}

/// Compute the total provisioning needed for a passage. Returns one
/// `ProvisioningCalc` per consumable.
///
/// `crewCount` = number of people on board.
/// `days` = expected duration in days.
/// `level` = "minimum" | "rated" | "generous".
/// `reserveFraction` = additional fraction to carry on top
/// (defaults to 0.3 — RYA-suggested 30% reserve).
export function provisioningPlan(opts: {
  crewCount: number;
  days: number;
  level?: "minimum" | "rated" | "generous";
  reserveFraction?: number;
}): readonly ProvisioningCalc[] {
  const level = opts.level ?? "rated";
  const reserveFraction = opts.reserveFraction ?? 0.3;
  return CONSUMABLES.map((c) => {
    const perDay =
      level === "minimum"
        ? c.minimumPerPersonPerDay
        : level === "generous"
          ? c.generousPerPersonPerDay
          : c.ratedPerPersonPerDay;
    const base = perDay * opts.crewCount * opts.days;
    return {
      consumable: c.id,
      perDay: perDay * opts.crewCount,
      totalQuantity: base,
      reserveQuantity: base * reserveFraction,
    };
  });
}

// ───────────────────────────────────────────────────────────────────
// 2. Crew safety brief — sectioned template
// ───────────────────────────────────────────────────────────────────

export type BriefSectionId =
  | "introductions"
  | "boat-orientation"
  | "safety-equipment"
  | "lifejackets-harnesses"
  | "mob-procedure"
  | "fire-procedure"
  | "abandon-ship"
  | "watch-system"
  | "communications"
  | "emergency-contacts";

export interface BriefSection {
  id: BriefSectionId;
  /// Section title — used as the heading on the printed brief.
  title: string;
  /// One-line gist of what the section covers.
  summary: string;
  /// Talking points the skipper covers — typically said while
  /// physically showing the location or demonstrating.
  talkingPoints: readonly string[];
  /// What to physically show or demonstrate during this section.
  demo: string;
}

export const CREW_BRIEF: readonly BriefSection[] = Object.freeze([
  {
    id: "introductions",
    title: "Introductions + roles",
    summary:
      "Who's who. The skipper introduces themselves + the boat, then asks each crew member to name any relevant experience and any health / medication issues.",
    talkingPoints: [
      "Skipper introduces self + the boat (name, length, type).",
      "Mate / first mate identified — second decision-maker.",
      "Each crew member names sailing experience + qualifications.",
      "Each crew member states any medication, allergies, conditions (especially anything that may affect their ability to operate at sea).",
      "Confirm shore-based emergency contact details for every crew member.",
    ],
    demo:
      "Walk through the boat together — saloon, heads, galley, cabins, navigator's station.",
  },
  {
    id: "boat-orientation",
    title: "Boat orientation",
    summary:
      "Where everything lives — galley, heads, fresh water, holding tank, fuel, gas, batteries, sea-cocks.",
    talkingPoints: [
      "Galley: cooker, gas isolator at the appliance + at the bottle, fresh-water pump + tank capacity, holding-tank pump + capacity.",
      "Heads: how to use, the flush sequence, sea-cock locations (intake + discharge).",
      "Fresh-water tank: capacity, gauge, pump, taps. Conservation policy.",
      "Fuel tank: capacity, range at cruising rpm, refuelling procedure.",
      "Gas locker: location, vent, isolator.",
      "Batteries: house vs start, isolator switch positions, voltage gauge, alternator + smart-charger arrangement.",
      "Sea-cocks: walk + identify each one, normal positions (open / closed).",
    ],
    demo:
      "Physical tour — open the heads, point to the sea-cocks, open the gas locker, locate the seacock chart on the navigator's station.",
  },
  {
    id: "safety-equipment",
    title: "Safety equipment — what + where",
    summary:
      "Every piece of safety equipment located + demonstrated. Lifejackets, harnesses, jackstays, flares, EPIRB, fire extinguishers + blanket, first-aid kit, grab bag, liferaft.",
    talkingPoints: [
      "Lifejackets — one per crew (sized correctly). Show the wearing rule (skipper's policy).",
      "Harnesses + tethers — clipping points, two-tether convention for going forward.",
      "Jackstays — port + starboard, clip on at the companionway BEFORE stepping out.",
      "Flares — location, expiry date, types carried + their uses.",
      "EPIRB — location, manual-activation procedure, hydrostatic-release explanation.",
      "Fire extinguishers + fire blanket — every location identified, type of fire each handles.",
      "First-aid kit — category (A/B/C/D), location, key contents.",
      "Grab bag — contents, location, who carries it on abandon-ship.",
      "Liferaft — location, painter, deployment procedure (covered in detail in abandon-ship section).",
    ],
    demo:
      "Physically touch every piece — open the flare box, show the EPIRB activation procedure, open the first-aid kit, demonstrate the fire extinguisher pin pull.",
  },
  {
    id: "lifejackets-harnesses",
    title: "Lifejacket + harness policy",
    summary:
      "The skipper's policy on when lifejackets + harnesses are worn — explicit, briefed, and followed without negotiation.",
    talkingPoints: [
      "Lifejackets worn at all times when on deck OUTSIDE sheltered harbours / marinas.",
      "Lifejackets worn at all times after sunset, regardless of conditions.",
      "Lifejackets worn in fog or visibility < 1 nm.",
      "Harnesses worn + clipped on when going forward, when single-watch on deck, in conditions over F6, when reefing, when working on the foredeck.",
      "Two-tether rule for going forward: clip one tether ON before unclipping the other, so you're never unclipped.",
      "Crew are EXPECTED to challenge the skipper if they feel a lifejacket / harness should be worn.",
    ],
    demo:
      "Each crew member fits their lifejacket + harness. Skipper checks fit + adjusts. Crew practise clipping on at the companionway.",
  },
  {
    id: "mob-procedure",
    title: "Man overboard — drill",
    summary:
      "The standard RYA MOB sequence — Shout, Point, Throw, Press MOB, VHF + the recovery manoeuvre suited to vessel + conditions.",
    talkingPoints: [
      "SHOUT 'MAN OVERBOARD!' — alerts every crew.",
      "POINT — a designated person points at the casualty continuously + does nothing else.",
      "THROW — lifebuoy / dan-buoy / MOB module straight in to mark + give the casualty something to hold.",
      "PRESS the MOB button on the chartplotter or GPS — marks the waypoint.",
      "VHF — Pan Pan or Mayday on Ch 16; DSC distress button if conditions warrant.",
      "Recovery manoeuvre: figure-of-eight (sail), Anderson turn (power), or Williamson turn (poor visibility / lost sight).",
      "All crew rehearse the recovery manoeuvre as part of the brief — practice it once with a fender as the casualty.",
    ],
    demo:
      "Drop a fender overboard + execute the recovery manoeuvre under sail or power. Each crew member takes a turn at being the lookout / VHF caller / helm.",
  },
  {
    id: "fire-procedure",
    title: "Fire — drill",
    summary:
      "Locate, contain, extinguish. The crew's job is to find the fire, get others out of the way, and put it out — in that order.",
    talkingPoints: [
      "On smelling smoke or flame: SHOUT 'FIRE!' + location.",
      "Crew nearest the fire: locate, identify type (electrical / gas / fuel / galley).",
      "Skipper isolates the fuel: gas at the bottle isolator, fuel at the engine isolator + ON shore power off.",
      "All crew except firefighter: out of the cabin, lifejackets on, into the cockpit.",
      "Firefighter: appropriate extinguisher (water = NEVER on electrical or fuel; CO2 = electrical; fire blanket = galley pan).",
      "If fire not under control in 1 min: Mayday on Ch 16, prepare to abandon ship.",
      "Stay LOW — smoke rises; cleaner air at the cabin sole.",
    ],
    demo:
      "Walk through the boat identifying ignition sources (galley stove, engine bay, battery compartment, electrical panel). Demonstrate the appropriate extinguisher at each location.",
  },
  {
    id: "abandon-ship",
    title: "Abandon ship — drill",
    summary:
      "Liferaft launch sequence + grab-bag contents. The decision to abandon ship is the skipper's — but every crew member should know the drill.",
    talkingPoints: [
      "ABANDON SHIP is a SKIPPER's decision — never abandoned unless the boat is going down faster than you can pump.",
      "GRAB BAG: who collects it + brings it on deck (typically the navigator).",
      "EPIRB: who collects it (often integrated in the liferaft pack but check).",
      "Final positions: handheld VHF on Ch 16 calling Mayday with position.",
      "Liferaft DEPLOYMENT: painter to a strong point on the boat (chain plate, mast cleat), then heave overboard on the LEEWARD side.",
      "Pull painter sharply until CO2 fires + raft inflates (~30 s).",
      "BOARD in order: strongest first (to help others in), then injured / weakest, then skipper LAST.",
      "STAY ATTACHED to the yacht while it is afloat — visibility, shelter, items to collect. Cut painter only when yacht threatens to drag the raft down.",
      "STREAM the drogue once clear; close canopy; activate EPIRB.",
    ],
    demo:
      "Show the liferaft + painter location. Walk through deployment slowly. Open the grab bag + verify contents (vhf, plb, water, flares, first-aid, food).",
  },
  {
    id: "watch-system",
    title: "Watch system",
    summary:
      "The hourly rotation that keeps a competent watchkeeper on deck + a rested crew ready to relieve them.",
    talkingPoints: [
      "Typical 4-on / 4-off for offshore passage with 4+ crew. 3-on / 3-off for short-handed.",
      "Watch leader has command of the boat during the watch — wakes the skipper for: any close-quarters situation, fog, blue flashing lights, system warnings, doubt.",
      "Hand-over: course, speed, course made good, vessels in sight, sail trim, any maintenance issue. Logged.",
      "Coffee + biscuits at the watch hand-over — wakes the relieving watch + clears the head of the off-going.",
      "Skipper available 24/7 — call them WITHOUT hesitation.",
    ],
    demo:
      "Demonstrate the log book entry format. Each watch leader practises a log entry + a verbal hand-over.",
  },
  {
    id: "communications",
    title: "Communications",
    summary:
      "VHF channel allocations, DSC use, who to call when. Plus mobile phone use + the shore-side contact protocol.",
    talkingPoints: [
      "VHF Ch 16: distress + calling. ALWAYS on + monitored.",
      "VHF Ch 70: DSC dedicated. Never voice on 70.",
      "VHF Ch 67: UK Coastguard SAR working.",
      "VHF Ch 80: UK marina working — used for berth allocations.",
      "DSC distress button: location + 5-second hold procedure (covered in safety equipment section).",
      "Pan Pan (urgency): when you need help but not life-threatening — e.g. engine failure, towing required.",
      "Securité (safety): broadcast safety information — used by Coastguard, sometimes by yachts spotting a hazard.",
      "Mobile phone: NOT a primary distress tool but useful supplement. Coastguard 999 + ask for Coastguard if in UK waters.",
    ],
    demo:
      "Show the VHF set's location + how to PTT. Demonstrate channel changes. If DSC fitted, walk through the menu structure (without firing the distress button!).",
  },
  {
    id: "emergency-contacts",
    title: "Emergency contacts — ashore",
    summary:
      "Who to call, what they need to hear, what they should do if they don't hear from you.",
    talkingPoints: [
      "Crew's shore-based emergency contact: name + phone for each crew member, stored at the navigator's station.",
      "Boat's home port + insurer contact details.",
      "Coastguard contact for the area you're sailing in — UK MRCC numbers; if foreign, the local MRCC.",
      "Skipper's pre-departure call to a designated shore contact: 'I'm leaving X for Y at HH:MM, expected arrival ZZ:ZZ, call Coastguard if no contact by AA:AA'.",
      "Skipper's arrival call to the same contact — releases the alert if it was logged.",
    ],
    demo:
      "Show the laminated contact sheet at the navigator's station. Confirm everyone's contact is on it.",
  },
]);

export const CREW_BRIEF_IDS: readonly BriefSectionId[] = CREW_BRIEF.map(
  (s) => s.id,
);
